import * as React from 'react'
import type { Moment } from 'moment'

import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { ActiveMenuItem } from '@/app/constants/menuItem'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  getReportNumberByType,
  getReportNameByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'
import { usePrintPdf } from './usePrintPdf'
import { getDefaultReportParams } from '../utils/reportDefaultParams'
import { useBatchPrintoutStore } from '../stores/batchPrintoutStore'
import { usePrintJobGuard } from './usePrintJobGuard'

/**
 * Raporty budowane jako jedna lista dla wszystkich kierowcow
 * (IsSingleListContent po stronie serwera, bez PrepareData na kierowce).
 * Wymuszenie osobnych plikow daje dla nich pusty dokument i blad "brak danych",
 * wiec zostaja przy wydruku zbiorczym - kosztem folderu w paczce ZIP.
 */
const SINGLE_DOCUMENT_REPORTS: ReadonlySet<ReportType> = new Set([
  ReportType.VIRTUAL_DISPLACEMENT,
  ReportType.GROUPED_TRIP_BASICS,
])

export interface GenerateReportsParams {
  companyId: string
  reports: ReportType[]
  activeItem: ActiveMenuItem
  checkedDrivers: string[]
  checkedVehicles: string[]
  dateFrom: Moment
  dateTo: Moment
  outputType: OutputType
  /**
   * Parametry uzyte ostatnio dla danego raportu w tej sesji - parytet z WPF,
   * gdzie wydruk grupowy korzysta z modelu raportu z cache fabryki.
   */
  lastUsedParams?: Map<ReportType, any>
  /**
   * Nazwy zaznaczonych kierowcow/pojazdow. Przy wiecej niz jednym raporty ida
   * do osobnych plikow (po jednym na kierowce), zeby paczka ZIP miala folder
   * na kazdego z nich.
   */
  subjectNames?: string[]
  /** Jedna paczka, po wygenerowaniu mail na adresy z konfiguracji e-mail. */
  sendByEmail?: boolean
  issuerSignature?: boolean
  driverSignature?: boolean
}

/**
 * Generowanie wielu raportow naraz - kazdy raport idzie osobnym zadaniem,
 * z domyslnymi parametrami wydruku (okna parametrow nie otwieramy).
 */
export const useBatchPrintout = (
  onTaskCreated?: (taskId: string, reportType: ReportType) => void,
) => {
  const { t } = useLocalize()
  const { printPdf } = usePrintPdf()
  const { isGenerating, runPrintJob } = usePrintJobGuard()
  const [sending, setSending] = React.useState(false)

  // Paczka trwa az do zebrania wszystkich dokumentow - druga paczka
  // wyczyscilaby stan pierwszej, wiec przycisk zostaje zablokowany.
  const batchInProgress = useBatchPrintoutStore(
    (s) => s.docs.length > 0 && s.docs.some((doc) => doc.status === 'pending'),
  )

  const generateBatch = React.useCallback(
    async ({
      companyId,
      reports,
      activeItem,
      checkedDrivers,
      checkedVehicles,
      dateFrom,
      dateTo,
      outputType,
      lastUsedParams,
      subjectNames = [],
      sendByEmail = false,
      issuerSignature = true,
      driverSignature = true,
    }: GenerateReportsParams) => {
      if (reports.length === 0) return

      setSending(true)

    

      const failed: ReportType[] = []
      const { startBatch, trackTask, sealBatch } = useBatchPrintoutStore.getState()

      // Wiecej niz jeden kierowca - raport per kierowca, zeby paczka ZIP mogla
      // miec folder na kazdego z nich.
      const subjectCount =
        activeItem === ActiveMenuItem.PRINTOUT_DRIVER
          ? checkedDrivers.length
          : checkedVehicles.length
      const separateFiles = subjectCount > 1

      startBatch(subjectNames)

      if (sendByEmail) {
        const reportsPayload = reports.map((report) => ({
          reportType: getReportNumberByType(report),
          data: {
            ...(lastUsedParams?.get(report) ?? getDefaultReportParams(report)),
            hideSignatures: false,
            issuerSignature,
            driverSignature,
            ...(separateFiles && !SINGLE_DOCUMENT_REPORTS.has(report)
              ? { separateFiles: true }
              : {}),
          },
        }))

        try {
          const { taskId } = await printPdf({
            companyId,
            reportType: reportsPayload[0].reportType,
            drivers:
              activeItem === ActiveMenuItem.PRINTOUT_DRIVER
                ? checkedDrivers
                : null,
            vehicles:
              activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
                ? checkedVehicles
                : null,
            startTime: dateFrom,
            endTime: dateTo,
            outputType,
            data: reportsPayload[0].data,
            sendByEmail: true,
            reports: reportsPayload,
          })

          trackTask(taskId, reports[0])
          onTaskCreated?.(taskId, reports[0])
        } catch (err) {
          failed.push(...reports)
          console.error('Exception in printPdf for emailed printouts:', err)
        }
      }

      for (const report of sendByEmail ? [] : reports) {
        const reportNumber = getReportNumberByType(report)

        try {
          const { taskId } = await printPdf({
            companyId,
            reportType: reportNumber,
            drivers:
              activeItem === ActiveMenuItem.PRINTOUT_DRIVER
                ? checkedDrivers
                : null,
            vehicles:
              activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
                ? checkedVehicles
                : null,
            startTime: dateFrom,
            endTime: dateTo,
            outputType,
            data: {
              ...(lastUsedParams?.get(report) ?? getDefaultReportParams(report)),
              hideSignatures: false,
              issuerSignature,
              driverSignature,
              ...(separateFiles && !SINGLE_DOCUMENT_REPORTS.has(report)
                ? { separateFiles: true }
                : {}),
            },
          })

          // Zadania paczki zbieramy w podsumowaniu zamiast otwierac podglad
          // kazdego dokumentu z osobna.
          trackTask(taskId, report)
          onTaskCreated?.(taskId, getReportTypeByNumber(reportNumber))
        } catch (err) {
          failed.push(report)
          console.error(`Exception in printPdf for report ${report}:`, err)
        }
      }

      sealBatch()

      const succeeded = reports.length - failed.length

    

      if (failed.length > 0) {
        toast.error({
          title: t('printout.generation-error'),
          text: failed
            .map((report) => t(getReportNameByType(report)))
            .join(', '),
          autoCloseMs: 5000,
        })
      }

      setSending(false)

      return { succeeded, failed }
    },
    [printPdf, onTaskCreated, t],
  )

  const generateReports = React.useCallback(
    (params: GenerateReportsParams) => runPrintJob(() => generateBatch(params)),
    [generateBatch, runPrintJob],
  )

  return {
    generateReports,
    pending: sending || batchInProgress || isGenerating,
  }
}
